import { useEffect, useState } from 'react';
import type { AdminWhatsappTemplateRow } from '../../../domain/superadmin/types';
import { AppIcon } from '../../../components/icons/AppIcon';
import { Button, Input } from '../../../components/ui';
import Modal from '../../../components/ui/Modal';
import { sendAdminTestWhatsapp } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';
import { whatsappSendErrorText } from './whatsappSendErrorText';

interface WhatsappTemplateTestSendModalProps {
    template: AdminWhatsappTemplateRow | null;
    isOpen: boolean;
    onClose: () => void;
    disabled?: boolean;
}

export function WhatsappTemplateTestSendModal({
    template,
    isOpen,
    onClose,
    disabled,
}: WhatsappTemplateTestSendModalProps) {
    const [to, setTo] = useState('');
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setTo('');
    }, [isOpen, template?.key]);

    if (!template) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const recipient = to.trim();
        if (!recipient) return;

        setIsSending(true);
        try {
            await sendAdminTestWhatsapp({ key: template.key, to: recipient });
            notify.success('WhatsApp de teste enviado com sucesso.');
            onClose();
        } catch (err) {
            if (err instanceof SuperadminApiError && err.isForbidden) {
                notify.warning('Acesso não autorizado à área de superadmin.');
                return;
            }
            if (err instanceof SuperadminApiError) {
                notify.error(whatsappSendErrorText(err.detail, err.message));
                return;
            }
            notify.error(err instanceof Error ? err.message : 'Falha ao enviar WhatsApp de teste.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" preventBackdropClose={isSending}>
            <Modal.Header onClose={onClose}>Enviar WhatsApp de teste</Modal.Header>
            <Modal.Body>
                <p className="text-sm text-secondary">
                    Envia o template <strong className="text-default">{template.name}</strong> com dados
                    de exemplo pela instância de sistema.
                </p>
                <form id="whatsapp-test-send-form" onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Número (com DDI)"
                        name="to"
                        type="tel"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="+55 11 99999-9999"
                        required
                        disabled={isSending || disabled}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex-col-reverse gap-2 sm:flex-row">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    className="w-full sm:w-auto"
                    disabled={isSending}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    form="whatsapp-test-send-form"
                    className="w-full sm:w-auto"
                    disabled={isSending || disabled || !to.trim()}
                >
                    <AppIcon name="send" className="h-4 w-4" />
                    {isSending ? 'Enviando...' : 'Enviar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
